import { useState, useEffect, useCallback } from 'react';
import { Input, Empty, Alert, Spin } from 'antd';
import { IconChevronRight, IconChevronDown } from '@tabler/icons-react';
import { useSizNorms } from '@/entities/siz';
import { PageSpinner } from '@/shared/ui/PageSpinner';
import type { PpeNormsByPosition, SizNormRule } from '@/entities/siz';
import s from './NormsTab.module.scss';

interface NormsTabProps {
  focusEmployeeId?: string;
}

const UNIT_LABELS: Record<string, string> = {
  piece: 'шт.',
  pair: 'пар',
  kit: 'компл.',
  set: 'набор',
};

const CATEGORY_LABEL_RU: Record<string, string> = {
  clothing: 'Спецодежда',
  footwear: 'Спецобувь',
  head_protection: 'Защита головы',
  eye_protection: 'Защита глаз',
  hand_protection: 'Защита рук',
  respiratory_protection: 'Защита органов дыхания',
  fall_protection: 'Защита от падения',
  hearing_protection: 'Защита слуха',
  other: 'Прочее',
};

function categoryLabelRu(category: string): string {
  return CATEGORY_LABEL_RU[category] ?? category;
}

/** Убирает хвост `.00` у целых и лишние нули у дробных. */
function formatNormQuantity(raw: string): string {
  const n = Number.parseFloat(raw);
  if (!Number.isFinite(n)) return raw.trim();
  if (Math.abs(n - Math.round(n)) < 1e-9) return String(Math.round(n));
  const s = n.toString();
  if (!s.includes('e') && s.includes('.')) {
    return s.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
  }
  return s;
}

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

function PositionNormsRow({ position }: { position: PpeNormsByPosition }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={s.root__positionRow}>
      <div className={s.root__positionHeader} onClick={() => setExpanded(!expanded)} role="button" tabIndex={0}>
        {expanded ? (
          <IconChevronDown size={16} className={s.root__chevronActive} aria-hidden />
        ) : (
          <IconChevronRight size={16} className={s.root__chevron} aria-hidden />
        )}
        <div className={s.root__positionInfo}>
          <div className={s.root__positionName}>{position.position.name}</div>
        </div>
        <span className={`${s.root__positionBadge} ${expanded ? s.root__positionBadge_active : ''}`}>
          {position.rules.length} норм
        </span>
      </div>

      {expanded ? (
        <div className={s.root__normsPanel}>
          <div className={s.root__normHeader}>
            <div>СИЗ</div>
            <div>Категория</div>
            <div>Кол-во</div>
            <div>Период</div>
          </div>
          {position.rules.length === 0 ? (
            <div className={s.root__empty}>Нет норм для этой профессии</div>
          ) : (
            position.rules.map((rule: SizNormRule) => (
              <div key={rule.id} className={s.root__normRow}>
                <div>
                  <div className={s.root__sizName}>{rule.sizItem.name}</div>
                  <div className={s.root__sizMeta}>
                    {rule.sizItem.code} · {rule.sizItem.gostOrTrTs || '—'}
                  </div>
                </div>
                <div>
                  <span className={s.root__categoryTag}>{categoryLabelRu(rule.sizItem.category)}</span>
                </div>
                <div className={s.root__qtyCell}>
                  {formatNormQuantity(rule.quantity)} {UNIT_LABELS[rule.sizItem.unit] ?? rule.sizItem.unit}
                </div>
                <div className={s.root__periodCell}>{rule.periodMonths ? `${rule.periodMonths} мес.` : '—'}</div>
              </div>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

export function NormsTab(_props: NormsTabProps) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);

  const { data, isLoading, isError, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useSizNorms({
    q: debouncedSearch || undefined,
  });

  const positions = data?.pages.flatMap((page) => page.positions) || [];

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      if (target.scrollHeight - target.scrollTop - target.clientHeight < 100 && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  if (isLoading && !data) {
    return <PageSpinner />;
  }

  return (
    <div className={s.root}>
      <div className={s.root__toolbar}>
        <div className={s.root__toolbarTitle}>Нормы выдачи СИЗ по профессиям</div>
        <Input.Search
          placeholder="Поиск по профессии…"
          className={s.root__search}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
        />
      </div>

      {isError && (
        <Alert
          type="error"
          message="Ошибка загрузки норм"
          description={error instanceof Error ? error.message : 'Неизвестная ошибка'}
          style={{ marginBottom: 16 }}
        />
      )}

      {debouncedSearch && isFetchingNextPage && (
        <div className={s.root__fetching}>
          <Spin size="small" />
          <span>Поиск…</span>
        </div>
      )}

      {positions.length === 0 && !isLoading && (
        <Empty
          description={
            debouncedSearch
              ? `Профессии по запросу «${debouncedSearch}» не найдены`
              : 'Нет данных о нормах СИЗ. Запустите seed-скрипт.'
          }
          style={{ marginTop: 40 }}
        />
      )}

      {positions.length > 0 && (
        <div className={s.root__scrollContainer} onScroll={handleScroll}>
          <div className={s.root__list}>
            {positions.map((pos: PpeNormsByPosition) => (
              <PositionNormsRow key={pos.position.id} position={pos} />
            ))}
          </div>
          {isFetchingNextPage && (
            <div className={s.root__footerSpin}>
              <Spin size="small" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
